<template>
  <footer class="bg-anthracite-800 text-white" data-testid="footer">
    <div :class="isReduced ? 'mx-auto max-w-[1160px]' : 'container mx-auto'">
      <!-- Top logo row -->
      <div
        class="flex flex-col gap-6 border-t border-gray-700 px-4 py-6 md:px-0 md:pt-10"
      >
        <!-- Navigation columns - Desktop -->
        <div
          class="hidden grid-cols-2 gap-x-10 gap-y-8 sm:grid-cols-3 md:grid md:grid-cols-4"
        >
          <SFFooterNavColumn
            v-for="column in navigationColumns"
            :key="column.name"
            :column="column"
          />
          <div
            v-if="!isReduced"
            class="space-y-2 [&_a:hover]:text-orange-500 [&_a]:transition-colors"
            data-testid="footer-column"
          >
            <div>
              <h3 class="mb-4 font-sans text-xl font-medium">
                {{ $t('footer.contact') }}
              </h3>
              <div class="mb-4 flex items-center gap-3">
                <IconContact class="size-10 bg-anthracite-800" />
                <SFLink
                  :to="getLocalizedRoute('/s/contact-us')"
                  class="text-lg font-medium"
                  raw
                >
                  {{ $t('footer.contact_teaser') }}
                </SFLink>
              </div>
            </div>
          </div>
        </div>

        <div v-if="!isReduced" class="border-b border-gray-600 md:hidden">
          <div class="py-4">
            <h3 class="mb-5 font-sans text-xl font-semibold">
              {{ $t('footer.contact') }}
            </h3>
            <div class="mb-4 flex items-center gap-3">
              <IconContact class="size-10 bg-anthracite-800" />
              <SFLink
                :to="getLocalizedRoute('/s/contact-us')"
                class="text-lg font-medium"
                raw
              >
                {{ $t('footer.contact_teaser') }}
              </SFLink>
            </div>
          </div>
        </div>
        <!-- Navigation columns - Mobile -->
        <SFFooterMobileNav :columns="navigationColumns" />
      </div>

      <div
        v-if="!isReduced"
        class="border-t border-gray-700 px-4 py-6 pb-3 xs:border-t-0 xs:px-4 md:border-t md:px-0 md:py-10 md:pb-2"
      >
        <div class="flex flex-col gap-8 md:flex-row">
          <SFFooterPaymentSection :payments="payments" />
          <SFFooterSocialSection :follow-us="followUs" />
        </div>
      </div>

      <SFFooterCategories v-if="!isReduced" :categories="topCategories" />
    </div>
  </footer>
</template>

<script lang="ts" setup>
import { defineAsyncComponent } from 'vue'
import { SFLink } from '#storefront-ui/components'
import { useRouteHelpers } from '~/composables'
import { useFooterData } from '~/composables/useFooterData'

// Define child components as async to defer their loading
const SFFooterNavColumn = defineAsyncComponent(
  () => import('./SFFooterNavColumn.vue'),
)
const SFFooterPaymentSection = defineAsyncComponent(
  () => import('./SFFooterPaymentSection.vue'),
)
const SFFooterSocialSection = defineAsyncComponent(
  () => import('./SFFooterSocialSection.vue'),
)
const SFFooterCategories = defineAsyncComponent(
  () => import('./SFFooterCategories.vue'),
)
const SFFooterMobileNav = defineAsyncComponent(
  () => import('./SFFooterMobileNav.vue'),
)

const isReduced = defineModel('reduced', {
  type: Boolean,
  default: false,
})

const { getLocalizedRoute } = useRouteHelpers()

const { navigationColumns, topCategories, payments, followUs } =
  await useFooterData()
</script>
